package game.entity

import com.soywiz.klock.TimeSpan
import com.soywiz.klock.milliseconds
import com.soywiz.klock.minutes
import com.soywiz.korio.lang.format
import com.soywiz.korma.geom.Point
import com.soywiz.korma.geom.Rectangle
import com.soywiz.korma.geom.contains
import game.*
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sign
import kotlin.random.Random

open class RinkEntity(
    val rink: RinkBattle,
    val homeCol: Int,
    val homeRow: Int,
    val bounds: Rectangle = Rectangle(0, 0, ROOM_WIDTH, ROOM_HEIGHT)
) : Entity() {
    val vel = Point()
    val pos = Point()
    val target = Point()
    open val speed = 0.1

    var puck: RinkPuck? = null

    init {
        col = homeCol
        row = homeRow
        pos.x = col.toDouble()
        pos.y = row.toDouble()
    }

    fun returnHome() {
        target.x = homeCol.toDouble() + 0.5
        target.y = homeRow.toDouble() + 0.5
    }

    val isHome: Boolean
        get() = col == homeCol && row == homeRow

    fun targetPuck() {
        target.copyFrom(rink.puck.pos)
    }

    fun moveToTarget(room: Room) {
        val distance = pos.distanceTo(target)

        val deltaCol = target.x - pos.x
        val deltaRow = target.y - pos.y

        if (distance > 0.0) {
            vel.x = deltaCol / distance * speed
            vel.y = deltaRow / distance * speed
        } else {
            vel.x = 0.0
            vel.y = 0.0
        }

        val targetCol = (pos.x + vel.x).toInt()
        val targetRow = (pos.y + vel.y).toInt()

        val entityAt = room.getEntityAt(targetCol, targetRow)

        if (entityAt == rink.puck && rink.mode == RinkBattleMode.Play) {
            rink.puck.setNewOwner(this)
        }

        val canMove = rink.faceOff // Ignore walls during faceoff
                || (room[targetCol, targetRow].type != TileType.Wall
                && (entityAt == null || (entityAt == rink.puck || entityAt == this)))
        if (canMove) {
            pos.x += vel.x
            pos.y += vel.y

            if (pos.x < bounds.left) pos.x = bounds.left
            if (pos.x > bounds.right) pos.x = bounds.right
            if (pos.y < bounds.top) pos.y = bounds.top
            if (pos.y > bounds.bottom) pos.y = bounds.bottom
        }

        col = pos.x.toInt()
        row = pos.y.toInt()
    }

}

class RinkShadowPlayer(rink: RinkBattle, homeCol: Int, homeRow: Int) : RinkEntity(rink, homeCol, homeRow) {
    override val speed: Double
        get() = 0.2

    override fun onInit(game: Game, room: Room) {
    }

    override fun onTick(game: Game, room: Room) {
        if (rink.faceOff) {
            // Move player towards puck.
            moveToTarget(room)
            game.player.col = col
            game.player.row = row
            game.player.slidingColVel = 0
            game.player.slidingRowVel = 0
        } else {
            game.player.also {
                col = it.col
                row = it.row
                pos.x = col.toDouble()
                pos.y = row.toDouble()

                if (it.colVel != 0 || it.rowVel != 0) {
                    vel.x = it.colVel.toDouble()
                    vel.y = it.rowVel.toDouble()
                }

                if (it.attacking) {
                    puck?.shoot(game, it.col, it.row, it.col + vel.x * 5, it.row + vel.y * 5)
                }
            }
        }
    }
}

class RinkPlayer(rink: RinkBattle, bounds: Rectangle, homeCol: Int, homeRow: Int) :
    RinkEntity(rink, homeCol, homeRow, bounds) {
    val goal = Point(46, 10)

    override val speed: Double
        get() = min(0.1 + rink.scoreBoard.scoreDifference.toDouble() / 20.0, 0.25)

    override fun onInit(game: Game, room: Room) {
        sprite = game.getSpriteById("npc-dark-red")
    }

    override fun onTick(game: Game, room: Room) {
        val puck = this.puck
        if (rink.faceOff) {
            // Leave target as is
        } else if (puck != null) {
            val shoot = when (this) {
                rink.leftDefence, rink.rightDefence -> {
                    targetPuck()
                    pos.distanceTo(rink.center.pos) < puck.maxDistance
                }
                else -> {
                    // center
                    target.copyFrom(goal)
                    pos.distanceTo(goal) < puck.maxDistance
                }
            }

            if (shoot) {
                puck.shoot(game, col, row, target.x, target.y)
            }

        } else {
            target.x = bounds.x + bounds.width / 2
            target.y = bounds.y + bounds.height / 2

            if (bounds.contains(rink.puck.pos) && rink.puck.owner != rink.center) {
                targetPuck()
            }
        }
        moveToTarget(room)
    }

    override fun onCollideWithPlayer(game: Game, room: Room): Boolean {
        return false
    }
}

class RinkPuck(val rink: RinkBattle) : Entity() {
    val maxDistance = 15
    var distance = 0
    val maxVel = 1.0
    val vel = Point()
    val pos = Point()
    val playerGoal = Rectangle(46.0, 9.0, 1.0, 3.0)
    val computerGoal = Rectangle(4.0, 9.0, 1.0, 3.0)

    var owner: RinkEntity? = null
    var lastOwner: RinkEntity? = null
    var ownedCoolDown = 0
    var moveCoolDown = 0

    override fun onInit(game: Game, room: Room) {
        sprite = game.getSpriteById("small-shard")
    }

    override fun onTick(game: Game, room: Room) {
        if (ownedCoolDown > 0) {
            ownedCoolDown--
        }

        val owner = owner
        if (owner != null) {
            if (abs(owner.vel.x) > abs(owner.vel.y)) {
                pos.x = owner.col + sign(owner.vel.x)
                pos.y = owner.pos.y
            } else {
                pos.x = owner.pos.x
                pos.y = owner.row + sign(owner.vel.y)
            }
        } else if (distance > 0) {
            if (moveCoolDown <= 0) {
                if (room[(pos.x + vel.x).toInt(), row].type == TileType.Wall) {
                    vel.x = -vel.x
                }
                if (room[col, (pos.y + vel.y).toInt()].type == TileType.Wall) {
                    vel.y = -vel.y
                }

                pos.x += vel.x
                pos.y += vel.y

                distance--
                moveCoolDown = 1
            } else {
                moveCoolDown--
            }
        }

        col = pos.x.toInt()
        row = pos.y.toInt()

        if (rink.mode == RinkBattleMode.Play) {
            if (playerGoal.contains(pos)) {
                rink.scoreBoard.computerScore++
                rink.prepareFaceOff(game)
            } else if (computerGoal.contains(pos)) {
                rink.scoreBoard.playerScore++
                rink.prepareFaceOff(game)
            }
        }
    }

    override fun onCollideWithPlayer(game: Game, room: Room): Boolean {
        if (rink.mode == RinkBattleMode.Play) {
            setNewOwner(rink.shadow)
        }

        return true
    }

    fun setNewOwner(newOwner: RinkEntity?) {
        if (ownedCoolDown > 0 && newOwner == lastOwner) {
            // Too early to switch owner. Skip!
        } else if (newOwner == owner) {
            // Same owner. Skip!
        } else {
            lastOwner = owner
            owner?.puck = null
            owner = newOwner
            newOwner?.puck = this

            ownedCoolDown = 10
        }
    }

    fun shoot(game: Game, startCol: Int, startRow: Int, targetCol: Double, targetRow: Double) {
        col = startCol
        row = startRow

        val targetDistance = Point(col, row).distanceTo(targetCol, targetRow)

        if (targetDistance > 0.0) {
            vel.x = (targetCol - col.toDouble()) / targetDistance * maxVel
            vel.y = (targetRow - row.toDouble()) / targetDistance * maxVel
        }

        pos.x = col.toDouble()
        pos.y = row.toDouble()

        setNewOwner(null)

        distance = maxDistance

        game.playSound("shoot-puck")
    }
}

class ScoreBoard(val onDone: (ScoreBoard) -> Unit) : Entity() {
    val done: Boolean
        get() = currentTime <= 0.minutes
    var playerScore = 0
    var computerScore = 0
    var currentTime = 5.minutes
    val scoreDifference: Int
        get() = max(playerScore - computerScore, 0)

    override fun onInit(game: Game, room: Room) {
    }

    override fun onTick(game: Game, room: Room) {
        currentTime -= 18.milliseconds

        val text =
            "%02d  %02d:%02d  %02d".format(
                playerScore,
                currentTime.minutes,
                currentTime.seconds % 60,
                computerScore
            )

        val roomMid = ROOM_WIDTH / 2
        val textMid = text.length / 2
        val startCol = roomMid - textMid
        for (i in 0 until text.length) {
            room[i + startCol, 0].char = text[i].code
        }

        if (currentTime <= TimeSpan.ZERO || playerScore > 99 || computerScore > 99) {
            onDone.invoke(this)
        }
    }
}

enum class RinkBattleMode {
    MoveToFaceOff,
    FaceOff,
    Play,
}

class RinkBattle : Entity() {
    var mode = RinkBattleMode.FaceOff
    val faceOff
        get() = mode == RinkBattleMode.FaceOff || mode == RinkBattleMode.MoveToFaceOff
    var faceOffCountdown = 0

    val scoreBoard = ScoreBoard() {
    }

    val shadow = RinkShadowPlayer(this, 29, 10)
    val leftDefence = RinkPlayer(this, Rectangle.fromBounds(3.0, 1.0, 28.0, 12.0), 14, 7)
    val rightDefence = RinkPlayer(this, Rectangle.fromBounds(3.0, 8.0, 28.0, 19.0), 14, 13)
    val center = RinkPlayer(this, Rectangle.fromBounds(3.0, 1.0, 50.0, 20.0), 21, 10)

    val puck = RinkPuck(this)

    override fun onInit(game: Game, room: Room) {
        room.addEntity(scoreBoard)

        room.addEntity(shadow)
        room.addEntity(leftDefence)
        room.addEntity(rightDefence)
        room.addEntity(center)
//        room.addEntity(puck)

        prepareFaceOff(game)
    }

    override fun onTick(game: Game, room: Room) {
        if (scoreBoard.done) {
            if (scoreBoard.playerScore == scoreBoard.computerScore) {
                // No ties! Continue play until goal score
            } else if (scoreBoard.playerScore > scoreBoard.computerScore) {
                game.startTeleportTransition(game.player.col, game.player.row, 25, 10, "wishlair-2", true)
            } else {
                game.player.life = 0
            }
        }
        when (mode) {
            RinkBattleMode.MoveToFaceOff -> {
                if (leftDefence.isHome && rightDefence.isHome && center.isHome && shadow.isHome) {
                    mode = RinkBattleMode.FaceOff
                    faceOffCountdown = Random.nextInt(40, 100)
                    game.currentRoom.removeEntity(puck)
                }
            }
            RinkBattleMode.FaceOff -> {
                if (faceOffCountdown > 0) {
                    faceOffCountdown--
                } else {
                    mode = RinkBattleMode.Play
                    room.addEntity(puck)
                    puck.pos.x = 25.5
                    puck.pos.y = 10.5
                    game.player.sliding = false

                }
            }
            RinkBattleMode.Play -> {
                // Nothing to do...
            }
        }
    }

    fun prepareFaceOff(game: Game) {
        mode = RinkBattleMode.MoveToFaceOff

        leftDefence.returnHome()
        rightDefence.returnHome()
        center.returnHome()
        shadow.returnHome()

        puck.setNewOwner(null)
        puck.moveCoolDown = 0
    }
}
